// What a workflow version needs before it can run (docs/12 "Requirements"), and the skills it
// gives its agents, readable before anyone downloads it.
import { BookOpen, CaretDown, Desktop, Key, PlugsConnected } from "@phosphor-icons/react";

import { Markdown } from "../../markdown/Markdown";
import type { Requirement, Skill } from "../../workflows/portable";

const KIND_LABEL: Record<string, string> = {
  anthropic: "Claude model key (or the platform's)",
  anthropic_api_key: "Anthropic API key",
  anthropic_oauth_token: "Claude subscription token",
  github_pat: "GitHub token",
  github_app: "GitHub App",
  jira_token: "Jira token",
  slack_bot: "Slack bot",
  db: "Database credential",
  aws_sso: "AWS sign-in",
  vpn_profile: "VPN profile",
  generic: "Secret",
};

function describe(r: Requirement): { Icon: typeof Key; title: string; note: string } {
  switch (r.kind) {
    case "credential":
      return {
        Icon: Key,
        title: KIND_LABEL[r.credential_kind] ?? r.credential_kind,
        note: [r.required ? "required" : "optional", r.platform_default ? "platform key allowed" : null, r.name ? `named “${r.name}”` : null].filter(Boolean).join(" · "),
      };
    case "oauth":
      return { Icon: PlugsConnected, title: `${r.provider} account`, note: r.required ? "required" : "optional" };
    case "isolation":
      return { Icon: Desktop, title: "Its own virtual machine", note: "runs isolated, not in a shared container" };
  }
}

export function Requirements({ requirements }: { requirements: Requirement[] }) {
  return (
    <div className="card p-6">
      <p className="kicker">Needs</p>
      {requirements.length === 0 ? (
        <p className="mt-3 text-ink2">Nothing beyond a model.</p>
      ) : (
        <ul className="mt-4 space-y-3">
          {requirements.map((r, i) => {
            const d = describe(r);
            return (
              <li key={i} className="flex gap-3">
                <d.Icon weight="duotone" className="mt-0.5 h-5 w-5 shrink-0 text-ink" aria-hidden />
                <div>
                  <p className="font-semibold leading-tight">{d.title}</p>
                  <p className="text-[13.5px] text-ink3">{d.note}</p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <p className="mt-5 border-t border-line pt-4 text-[13px] text-ink3">Keys are bound when a team installs the workflow and stay in the broker. The agents never see them.</p>
    </div>
  );
}

const stripFront = (s: string) => s.replace(/^---\n[\s\S]*?\n---\n/, "");

export function Skills({ skills }: { skills: Skill[] }) {
  if (skills.length === 0) return null;
  return (
    <div className="card p-6">
      <p className="kicker">Skills it gives its agents · {skills.length}</p>
      <div className="mt-4 divide-y divide-line">
        {skills.map((s) => {
          const main = s.files.find((f) => f.path === "SKILL.md");
          return (
            <details key={s.slug} className="group py-3">
              <summary className="flex cursor-pointer list-none items-start gap-3">
                <BookOpen weight="duotone" className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
                <span className="flex-1">
                  <span className="block font-semibold leading-tight">{s.name}</span>
                  <span className="mt-0.5 block text-[13.5px] text-ink3">{s.description}</span>
                </span>
                <CaretDown weight="bold" className="mt-1 h-4 w-4 text-ink3 transition-transform group-open:rotate-180" aria-hidden />
              </summary>
              {main ? (
                <div className="mt-3 max-h-96 overflow-y-auto rounded-xl bg-paper2/60 p-4">
                  <Markdown source={stripFront(main.content)} className="!text-[15px] !leading-relaxed" />
                </div>
              ) : null}
            </details>
          );
        })}
      </div>
    </div>
  );
}
